import { Truck, PackageCheck, ClipboardList, Check, ArrowRight, History, Sparkles } from "lucide-react";
import { stageViews, STAGE_TYPE_LABEL } from "@/lib/stages";
import { isStageNew } from "@/components/orders/BomRevisionBanner";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN"); // production units are whole pieces

function tone(v) {
  if (v.isDone) return { border: "border-emerald-300", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", label: "Done" };
  if (v.isActive || v.available > 0) {
    return v.isOsp
      ? { border: "border-orange-400 bg-orange-50/40", badge: "bg-orange-100 text-orange-700", dot: "bg-orange-500", label: "Active" }
      : { border: "border-blue-300", badge: "bg-blue-100 text-blue-700", dot: "bg-blue-500", label: "Active" };
  }
  return { border: "border-slate-200", badge: "bg-slate-100 text-slate-500", dot: "bg-slate-300", label: "Waiting" };
}

function Metric({ label, value, cls = "", hint }) {
  return (
    <div className="rounded bg-white/80 border border-slate-100 p-2">
      <p className="text-[10px] text-slate-400 leading-tight">{label}</p>
      <p className={`font-mono-data text-xs font-semibold ${cls}`}>{value}{hint && <span className="text-slate-400 font-normal"> {hint}</span>}</p>
    </div>
  );
}

function StageCard({ order, v, target, canManage, onLog, onShip, onReceive }) {
  const t = tone(v);
  const code = order.order_code;
  const stageDef = (order.stages || [])[v.index];
  const isNew = isStageNew(stageDef);
  const typeLabel = v.isOsp ? `${STAGE_TYPE_LABEL.osp}${v.vendorName ? ` · ${v.vendorName}` : ""}` : `${STAGE_TYPE_LABEL[v.type] || "In-House"}${v.isFinal ? " (Final Stage)" : ""}`;
  let logLabel = "Log Quantity";
  if (v.isDone) logLabel = "Log Quantity · Complete";
  else if (v.waitingOn) logLabel = `Waiting for ${v.waitingOn}`;
  const logDisabled = v.isDone || v.available <= 0;
  return (
    <div className={`flex-1 min-w-[210px] rounded-lg border-2 bg-white p-3 ${isNew ? "border-blue-500 ring-2 ring-blue-200 bg-blue-50/40" : t.border}`} data-testid={`stage-card-${code}-${v.index}`}>
      {isNew && (
        <span className="mb-1.5 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-600 text-white" data-testid={`stage-new-${code}-${v.index}`}>
          <Sparkles className="h-2.5 w-2.5" /> New / Changed
        </span>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-6 w-6 shrink-0 rounded-full text-white text-[11px] flex items-center justify-center font-semibold ${t.dot}`}>
            {v.isDone ? <Check className="h-3.5 w-3.5" /> : v.index + 1}
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate">{v.index + 1} · {v.name}</p>
            <p className={`text-[10px] truncate ${v.isOsp ? "text-orange-600" : "text-blue-600"}`}>{typeLabel}</p>
          </div>
        </div>
        <span className={`text-[10px] font-mono-data px-1.5 py-0.5 rounded shrink-0 ${t.badge}`} data-testid={`stage-status-${code}-${v.index}`}>{t.label}</span>
      </div>

      {v.isOsp ? (
        <div className="grid grid-cols-2 gap-1.5 text-[11px]">
          <Metric label="Available to Send" value={fmt(v.available)} />
          <Metric label="Sent" value={fmt(v.shipped)} cls="text-orange-700" />
          <Metric label="At Vendor" value={fmt(v.atVendor)} cls="text-orange-700" />
          <Metric label="Received" value={`${fmt(v.done)} / ${fmt(target)}`} cls="text-emerald-700" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-1.5 text-[11px]">
          <Metric label="Available to Start" value={fmt(v.available)} cls={v.available > 0 ? "text-blue-700" : ""} />
          <Metric label={v.isFinal ? "Completed → FG Stock" : "Completed"} value={`${fmt(v.done)} / ${fmt(target)}`} cls="text-emerald-700" />
        </div>
      )}

      <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden flex">
        <div className="h-full bg-emerald-500" style={{ width: `${v.pct}%` }} />
        {v.isOsp && <div className="h-full bg-orange-400" style={{ width: `${v.vendorPct}%` }} />}
      </div>

      {v.isOsp ? (
        canManage ? (
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => onShip(order, v.index)} disabled={v.available <= 0}
              className="flex-1 h-8 rounded-md bg-orange-600 text-white text-[11px] font-medium hover:bg-orange-700 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1"
              title={v.available <= 0 ? (v.waitingOn ? `Waiting for ${v.waitingOn}` : "Full order quantity already sent") : "Send a (partial) quantity to the vendor"}
              data-testid={`stage-ship-${code}-${v.index}`}>
              <Truck className="h-3.5 w-3.5" /> Ship to Vendor
            </button>
            <button type="button" onClick={() => onReceive(order, v.index)} disabled={v.atVendor <= 0}
              className="flex-1 h-8 rounded-md border border-emerald-300 bg-white text-emerald-700 text-[11px] font-medium hover:bg-emerald-50 disabled:border-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1"
              title={v.atVendor <= 0 ? "Nothing pending at the vendor" : "Receive processed parts back"}
              data-testid={`stage-receive-${code}-${v.index}`}>
              <PackageCheck className="h-3.5 w-3.5" /> Receive{v.atVendor > 0 ? ` · ${fmt(v.atVendor)} pending` : ""}
            </button>
          </div>
        ) : (
          <p className="mt-2 text-[10px] text-slate-400 italic text-center">{v.atVendor > 0 ? `${fmt(v.atVendor)} pending at vendor` : "Ship / Receive by planner"}</p>
        )
      ) : (
        <button type="button" onClick={() => onLog(order, v.index)} disabled={logDisabled}
          className="mt-2 w-full h-8 rounded-md bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1"
          title={v.isDone ? "Full order quantity already processed at this stage" : v.waitingOn ? `Waiting for hand-off from ${v.waitingOn}` : "Log quantity completed at this stage"}
          data-testid={`stage-log-${code}-${v.index}`}>
          <ClipboardList className="h-3.5 w-3.5" /> {logLabel}
        </button>
      )}
    </div>
  );
}

export function StageCards({ order, canManage, onLog, onShip, onReceive, onHistory }) {
  const views = stageViews(order);
  const target = Number(order.target_quantity) || 0;
  if (!views.length) return <p className="text-xs text-slate-400 italic">This order has no stage pipeline.</p>;
  const finalName = views[views.length - 1].name;
  const moves = (order.stage_history || []).length;
  return (
    <div data-testid={`stage-cards-${order.order_code}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Stage-Gate · {moves} move{moves !== 1 ? "s" : ""}</p>
        {onHistory && (
          <button type="button" onClick={() => onHistory(order)} className="h-7 px-2 rounded-md inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-100" data-testid={`stage-history-${order.order_code}`}>
            <History className="h-3.5 w-3.5" /> View History
          </button>
        )}
      </div>
      <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
        {views.map((v, i) => (
          <div key={`${v.name}-${i}`} className="flex items-stretch gap-2 flex-1 min-w-0">
            <StageCard order={order} v={v} target={target} canManage={canManage} onLog={onLog} onShip={onShip} onReceive={onReceive} />
            {i < views.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300 self-center shrink-0" />}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        Order stays <b>In Progress</b> until <b>{finalName}</b> has completed all {fmt(target)} pcs — only then it becomes <b>Completed</b> and its finished goods are visible to Sales Fulfilment.
      </p>
    </div>
  );
}
